'use client';

import { Form, Nimbus3Scope } from '@gartner-digital-markets/nimbus-v3-ui';
import { zodResolver } from '@hookform/resolvers/zod';
import type { ReactNode } from 'react';
import { useEffect, useMemo, useRef } from 'react';
import { type UseFormReturn, useForm } from 'react-hook-form';
import { z } from 'zod';
import { identifyVisitorByEmail } from '../browser/identity';
import { createGatewaySubmitAction } from '../browser/submission';
import {
  type AnalyticsFormName,
  type AutomaticModalTriggerConfig,
  type ClientDataContextProps,
  EAS_MODAL_TEMPLATE_LABELS,
  type EasModalSidePanelContent,
  type EasModalTriggerConfig,
  type EasModalVariant,
  type FormPayload,
  type FormTracking,
  type ModalDisplayFrequency,
  type ModalFormCloseSource,
  type OnServerLog,
  type SubmitFormResult,
  createEmailSchema,
} from '../core';
import {
  DEFAULT_SECOND_BUTTON_TEXT,
  DEFAULT_STEP2_SUBTITLE,
  DefaultQualificationStep,
  createQualificationStepDefaultValues,
  createQualificationStepSchema,
  resolveQualificationConfig,
} from './components/DefaultQualificationStep';
import { EmailField } from './components/EmailField';
import { FormFooter } from './components/FormFooter';
import { FormHeadline } from './components/FormHeadline';
import { FormModal } from './components/FormModal';
import { SubmissionErrorMessage } from './components/SubmissionErrorMessage';
import { SubmitButton } from './components/SubmitButton';
import { SuccessScreen } from './components/SuccessScreen';
import { useEmailStorage } from './hooks/useEmailStorage';
import { useFormAnalytics } from './hooks/useFormAnalytics';
import { useFormSubmission } from './hooks/useFormSubmission';
import { useMultiStepForm } from './hooks/useMultiStepForm';
import { useTriggeredModal } from './hooks/useTriggeredModal';
import { cn } from './ui/utils';

const emailSchema = z.object({
  emailAddress: createEmailSchema(),
});

type EmailFormValues = z.infer<typeof emailSchema>;

const TEMPLATE_NAME_BY_VARIANT = {
  minimal: 'eas-modal-minimal',
  minimalNew: 'eas-modal-minimal-new',
  twoColumn: 'eas-modal-two-column',
} as const satisfies Record<EasModalVariant, string>;

const CLOSE_DETAIL_BY_SOURCE = {
  'close-button': 'close-button',
  'close-outside': 'close-outside',
  keyboard: 'close-button',
} as const satisfies Record<
  ModalFormCloseSource,
  'close-button' | 'close-outside'
>;

const DEFAULT_SIDE_PANEL_CONTENT: EasModalSidePanelContent = {
  eyebrow: 'Save your shortlist',
  title: 'Keep your software evaluation moving',
  description:
    'Send the recommendations to your inbox so you can revisit them when it is convenient.',
  bulletItems: [
    'Pick up where you left off later',
    'Share the list with your team',
    'Compare options without losing momentum',
  ],
};

const getTrackedCloseStep = (step: number): 1 | 2 => (step === 0 ? 1 : 2);
const DEFAULT_CUSTOM_TRIGGER = {
  type: 'custom',
} as const satisfies EasModalTriggerConfig;
const DEFAULT_AUTOMATIC_TRIGGER = {
  type: 'exit-intent',
} as const satisfies AutomaticModalTriggerConfig;

const getDefaultAnalyticsFormName = (
  trigger: EasModalTriggerConfig
): AnalyticsFormName =>
  trigger.type === 'custom' ? 'email-form-cta' : 'exit-intent';

export type ButtonPlacement = 'stacked' | 'inline';

export interface EmailStepRenderProps {
  emailForm: UseFormReturn<EmailFormValues>;
  handleSubmit: (event?: React.BaseSyntheticEvent) => Promise<void> | void;
  isLoading: boolean;
  buttonText: string;
  buttonPlacement: ButtonPlacement;
}

export interface QualificationStepRenderProps {
  qualificationForm: UseFormReturn<Record<string, unknown>>;
  handleSubmit: (event?: React.BaseSyntheticEvent) => Promise<void> | void;
  isLoading: boolean;
  buttonText: string;
}

export interface QualificationStepConfig {
  headline?: string;
  subtitle?: string;
  buttonText: string;
  defaultValues?: Record<string, unknown>;
  schema?: z.ZodTypeAny;
  render: (props: QualificationStepRenderProps) => React.ReactNode;
}

export interface ModalFormCommonProps {
  formId: string;
  lang?: string;
  variant?: EasModalVariant;
  isSingleStep?: boolean;
  headline?: string;
  subHeading?: string;
  buttonText?: string;
  step2Subtitle?: string;
  step2QuestionLabel?: string;
  qualificationOptions?: readonly string[] | null;
  isTimeline?: boolean;
  isPricing?: boolean;
  isCompanySize?: boolean;
  isFreeTrial?: boolean;
  secondButtonText?: string;
  successMessage?: string;
  successHeadline?: string;
  successIcon?: ReactNode;
  analyticsFormName?: AnalyticsFormName;
  contextProps?: ClientDataContextProps;
  tracking?: FormTracking;
  showRuler?: boolean;
  buttonPlacement?: ButtonPlacement;
  renderEmailStep?: (props: EmailStepRenderProps) => React.ReactNode;
  qualificationStep?: QualificationStepConfig;
  sidePanelContent?: EasModalSidePanelContent;
  width?: string;
  modalClassName?: string;
  gatewayUrl?: string;
  submitAction?: (payload: FormPayload) => Promise<SubmitFormResult>;
  onServerLog?: OnServerLog;
  termsUrl?: string;
  privacyUrl?: string;
}

export interface AutomaticModalFormBehaviorProps {
  trigger: AutomaticModalTriggerConfig;
  linkedFormIds?: readonly string[];
  displayFrequency?: ModalDisplayFrequency;
  allowedUrls?: readonly string[];
  restrictedUrls?: readonly string[];
  disabled?: boolean;
  skipModalTriggerCheck?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
}

export interface CustomModalFormProps extends ModalFormCommonProps {
  trigger?: typeof DEFAULT_CUSTOM_TRIGGER;
  isOpen: boolean;
  onClose: () => void;
  onOpen?: () => void;
}

export interface AutomaticModalFormProps
  extends ModalFormCommonProps,
    AutomaticModalFormBehaviorProps {
  isOpen?: never;
}

export type ModalFormProps = CustomModalFormProps | AutomaticModalFormProps;

export interface ModalFormBrandConfig {
  defaultTracking?: FormTracking;
  successIcon?: ReactNode;
  termsUrl?: string;
  privacyUrl?: string;
  submissionFailedMessageKey?: string;
}

type SharedModalFormProps = ModalFormProps & {
  brandConfig: ModalFormBrandConfig;
};

const TwoColumnSidePanel = ({
  sidePanelContent,
}: {
  sidePanelContent?: EasModalSidePanelContent;
}) => {
  const content = sidePanelContent ?? DEFAULT_SIDE_PANEL_CONTENT;
  const bulletItems = content.bulletItems ?? [];

  return (
    <aside className="hidden bg-nimbus-accent/5 px-8 py-9 sm:flex sm:flex-col sm:justify-center">
      {content.content ? (
        content.content
      ) : (
        <div className="space-y-5">
          {content.eyebrow ? (
            <p className="typo-10 font-semibold uppercase tracking-[0.12em] text-nimbus-accent-foreground">
              {content.eyebrow}
            </p>
          ) : null}
          {content.title ? (
            <h3 className="typo-70 font-bold text-nimbus-foreground">
              {content.title}
            </h3>
          ) : null}
          {content.description ? (
            <p className="typo-20 text-nimbus-muted-foreground">
              {content.description}
            </p>
          ) : null}
          {bulletItems.length > 0 ? (
            <ul className="space-y-3 text-sm text-nimbus-foreground">
              {bulletItems.map((item) => (
                <li
                  key={item}
                  className="flex items-start gap-3 leading-6 text-balance"
                >
                  <span
                    aria-hidden={true}
                    className="mt-2 h-2 w-2 rounded-full bg-nimbus-accent"
                  />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          ) : null}
          {content.footer ? (
            <p className="typo-10 text-nimbus-muted-foreground">
              {content.footer}
            </p>
          ) : null}
        </div>
      )}
    </aside>
  );
};

/**
 * Renders an email capture form inside a modal dialog with optional two-step qualification flow.
 *
 * This component displays a modal overlay for lead generation that can be triggered
 * automatically (exit-intent, scroll, timer) or programmatically. Supports multiple
 * visual variants including minimal, minimalNew, and twoColumn layouts.
 *
 * @example
 * ```tsx
 * import { ModalForm } from 'bx-forms/eas/capterra';
 *
 * // Automatic trigger (exit-intent)
 * <ModalForm
 *   formId="contentful-form-id"
 *   trigger={{ type: 'exit-intent' }}
 *   headline="Save your shortlist"
 * />
 *
 * // Custom trigger (controlled)
 * const [isOpen, setIsOpen] = useState(false);
 * <ModalForm
 *   formId="contentful-form-id"
 *   trigger={{ type: 'custom' }}
 *   isOpen={isOpen}
 *   onClose={() => setIsOpen(false)}
 * />
 * ```
 */
// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: multi-step modal with branching render logic
export const ModalForm = (props: SharedModalFormProps) => {
  const {
    formId,
    lang,
    variant = 'minimal',
    isSingleStep = false,
    headline = 'Send a copy of this list to my inbox',
    subHeading = 'Get this list delivered to your inbox so you can continue your research later.',
    buttonText = 'Send me the list',
    step2Subtitle = DEFAULT_STEP2_SUBTITLE,
    step2QuestionLabel,
    qualificationOptions,
    isTimeline,
    isPricing,
    isCompanySize,
    isFreeTrial,
    secondButtonText = DEFAULT_SECOND_BUTTON_TEXT,
    successMessage = "Your list will be delivered to your inbox shortly. If you don't see an email from Team Capterra in 5 minutes, please check your spam folder.",
    successHeadline = 'Thank you',
    successIcon,
    analyticsFormName: analyticsFormNameProp,
    contextProps,
    tracking,
    showRuler = false,
    buttonPlacement: buttonPlacementProp,
    renderEmailStep,
    qualificationStep,
    sidePanelContent,
    width,
    modalClassName,
    gatewayUrl = '/bx-capterra-api/bx-forms/submit',
    submitAction,
    onServerLog,
    termsUrl,
    privacyUrl,
    brandConfig,
  } = props;
  const buttonPlacement =
    buttonPlacementProp ?? (variant === 'minimalNew' ? 'inline' : 'stacked');
  const resolvedTrigger = props.trigger ?? DEFAULT_CUSTOM_TRIGGER;
  const isCustomTrigger = resolvedTrigger.type === 'custom';
  const analyticsFormName =
    analyticsFormNameProp ?? getDefaultAnalyticsFormName(resolvedTrigger);
  const customModalProps = isCustomTrigger
    ? (props as CustomModalFormProps)
    : null;
  const automaticModalProps = isCustomTrigger
    ? null
    : (props as AutomaticModalFormProps);
  const modalContextProps = useMemo(
    () => ({
      ...contextProps,
      pagePlacement: contextProps?.pagePlacement ?? 'modal',
    }),
    [contextProps]
  );

  const mergedTracking = useMemo(
    () => ({
      ...(brandConfig.defaultTracking ?? {}),
      contentCategory: EAS_MODAL_TEMPLATE_LABELS[variant],
      ...tracking,
    }),
    [brandConfig.defaultTracking, tracking, variant]
  );
  const portalBridgeAttributes = useMemo(() => {
    const brand = brandConfig.defaultTracking?.brand;

    if (!brand) {
      return undefined;
    }

    return {
      'data-bx-forms-brand': brand,
    };
  }, [brandConfig.defaultTracking?.brand]);

  const resolvedSubmitAction = useMemo(
    () => submitAction ?? createGatewaySubmitAction(gatewayUrl),
    [submitAction, gatewayUrl]
  );
  const { closeModal: closeTriggeredModal, isOpen: triggeredIsOpen } =
    useTriggeredModal({
      formId,
      linkedFormIds: automaticModalProps?.linkedFormIds,
      trigger: isCustomTrigger
        ? DEFAULT_AUTOMATIC_TRIGGER
        : (resolvedTrigger as AutomaticModalTriggerConfig),
      displayFrequency: automaticModalProps?.displayFrequency,
      allowedUrls: automaticModalProps?.allowedUrls,
      restrictedUrls: automaticModalProps?.restrictedUrls,
      disabled: Boolean(automaticModalProps?.disabled) || isCustomTrigger,
      skipModalTriggerCheck: automaticModalProps?.skipModalTriggerCheck,
      onOpen: automaticModalProps?.onOpen,
      onClose: automaticModalProps?.onClose,
    });
  const isOpen = isCustomTrigger
    ? (customModalProps?.isOpen ?? false)
    : triggeredIsOpen;
  const resolvedTermsUrl = termsUrl ?? brandConfig.termsUrl;
  const resolvedPrivacyUrl = privacyUrl ?? brandConfig.privacyUrl;
  const resolvedQualificationQuestionConfig = useMemo(
    () =>
      resolveQualificationConfig({
        lang,
        label: step2QuestionLabel,
        qualificationOptions,
        isTimeline,
        isPricing,
        isCompanySize,
        isFreeTrial,
      }),
    [
      lang,
      isCompanySize,
      isFreeTrial,
      isPricing,
      isTimeline,
      qualificationOptions,
      step2QuestionLabel,
    ]
  );
  const builtInQualificationSchema = useMemo(
    () =>
      createQualificationStepSchema(
        resolvedQualificationQuestionConfig.fieldName
      ),
    [resolvedQualificationQuestionConfig.fieldName]
  );
  const builtInQualificationDefaultValues = useMemo(
    () =>
      createQualificationStepDefaultValues(
        resolvedQualificationQuestionConfig.fieldName
      ),
    [resolvedQualificationQuestionConfig.fieldName]
  );
  const usesBuiltInQualificationStep = !(isSingleStep || qualificationStep);

  const defaultQualificationStep = useMemo<QualificationStepConfig | undefined>(
    () =>
      isSingleStep
        ? undefined
        : ({
            subtitle: step2Subtitle,
            buttonText: secondButtonText,
            defaultValues: builtInQualificationDefaultValues,
            schema: builtInQualificationSchema,
            render: ({ qualificationForm, handleSubmit, isLoading }) => (
              <DefaultQualificationStep
                form={qualificationForm}
                handleSubmit={handleSubmit}
                isLoading={isLoading}
                name={resolvedQualificationQuestionConfig.fieldName}
                buttonText={secondButtonText}
                label={resolvedQualificationQuestionConfig.label}
                options={resolvedQualificationQuestionConfig.options}
                buttonPlacement={buttonPlacement}
                lang={lang}
              />
            ),
          } satisfies QualificationStepConfig),
    [
      builtInQualificationDefaultValues,
      builtInQualificationSchema,
      buttonPlacement,
      isSingleStep,
      resolvedQualificationQuestionConfig,
      lang,
      secondButtonText,
      step2Subtitle,
    ]
  );
  const resolvedQualificationStep = useMemo(
    () =>
      isSingleStep
        ? undefined
        : (qualificationStep ?? defaultQualificationStep),
    [defaultQualificationStep, isSingleStep, qualificationStep]
  );
  const hasQualification = Boolean(resolvedQualificationStep);
  const totalSteps = hasQualification ? 3 : 2;
  const successStep = totalSteps - 1;

  const { step, formData, nextStep, reset } = useMultiStepForm({ totalSteps });
  const analytics = useFormAnalytics(formId, analyticsFormName, isOpen);
  const {
    submitToApi,
    resetSubmissionMetadata,
    submissionErrorMessage,
    isLoading,
  } = useFormSubmission({
    formId,
    tracking: mergedTracking,
    contextProps: modalContextProps,
    templateName: TEMPLATE_NAME_BY_VARIANT[variant],
    submitAction: resolvedSubmitAction,
  });
  const { getStoredEmail, setStoredEmail } = useEmailStorage();

  const emailForm = useForm<EmailFormValues>({
    resolver: zodResolver(emailSchema),
    defaultValues: { emailAddress: getStoredEmail() || '' },
    mode: 'onBlur',
  });

  const qualificationForm = useForm<Record<string, unknown>>({
    resolver: resolvedQualificationStep?.schema
      ? zodResolver(resolvedQualificationStep.schema)
      : undefined,
    defaultValues: resolvedQualificationStep?.defaultValues ?? {},
    mode: 'onBlur',
    shouldUnregister: true,
  });

  const wasOpenRef = useRef(isOpen);
  const resetEmailForm = emailForm.reset;
  const resetQualificationForm = qualificationForm.reset;

  useEffect(() => {
    if (usesBuiltInQualificationStep) {
      qualificationForm.reset(builtInQualificationDefaultValues);
    }
  }, [
    builtInQualificationDefaultValues,
    qualificationForm,
    usesBuiltInQualificationStep,
  ]);

  useEffect(() => {
    if (wasOpenRef.current && !isOpen) {
      reset();
      resetEmailForm({ emailAddress: getStoredEmail() || '' });
      resetQualificationForm(resolvedQualificationStep?.defaultValues ?? {});
      resetSubmissionMetadata();
    }

    if (isCustomTrigger && !wasOpenRef.current && isOpen) {
      customModalProps?.onOpen?.();
    }

    wasOpenRef.current = isOpen;
  }, [
    customModalProps?.onOpen,
    getStoredEmail,
    isCustomTrigger,
    isOpen,
    resolvedQualificationStep?.defaultValues,
    reset,
    resetEmailForm,
    resetQualificationForm,
    resetSubmissionMetadata,
  ]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    if (!formId) {
      analytics.trackFormFailedToDisplay();
      return;
    }

    const isPrefilled = !!getStoredEmail();
    analytics.trackFormOpen();
    analytics.trackFormExposeServer({
      formTemplate: TEMPLATE_NAME_BY_VARIANT[variant],
      formKind: 'modal',
      brand: mergedTracking.brand,
      placement: modalContextProps.pagePlacement,
      page_type: modalContextProps.pageType,
      page_path: window.location.pathname,
      eloqua_campaign: mergedTracking.eloquaCampaign,
    });
    if (isPrefilled) {
      analytics.trackFormPrefilled();
    }
  }, [
    analytics,
    formId,
    getStoredEmail,
    isOpen,
    mergedTracking.brand,
    mergedTracking.eloquaCampaign,
    modalContextProps.pagePlacement,
    modalContextProps.pageType,
    variant,
  ]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    if (hasQualification && step === 1) {
      analytics.trackMicroDataQuestionDisplayed();
    }
  }, [analytics, hasQualification, isOpen, step]);

  const handleSubmissionFailure = (result: SubmitFormResult) => {
    onServerLog?.(
      JSON.stringify({
        messageKey:
          brandConfig.submissionFailedMessageKey ??
          'bx_forms_submission_failed',
        message: `Form submission failed : ${JSON.stringify(result)}`,
        formId,
        contextProps: modalContextProps,
      })
    );
  };

  const handleEmailSubmit = async (data: EmailFormValues) => {
    const emailAddress = data.emailAddress;
    const result = await submitToApi({
      emailAddress,
      hasSecondPage: hasQualification,
    });
    if (result.success) {
      setStoredEmail(emailAddress);
      identifyVisitorByEmail(emailAddress);
      analytics.trackFormSubmitted(buttonText);
      nextStep(data);
    } else {
      analytics.trackFormFailed();
      handleSubmissionFailure(result);
    }
  };

  const handleQualificationSubmit = async (values: Record<string, unknown>) => {
    const result = await submitToApi({
      ...formData,
      ...values,
      hasSecondPage: false,
    });
    if (result.success) {
      analytics.trackMicroDataQuestionSubmitted();
      nextStep(values);
    } else {
      analytics.trackMicroDataQuestionFailed();
      handleSubmissionFailure(result);
    }
  };

  const handleModalClose = (source: ModalFormCloseSource) => {
    analytics.trackFormClosed(
      getTrackedCloseStep(step),
      CLOSE_DETAIL_BY_SOURCE[source]
    );

    if (isCustomTrigger) {
      customModalProps?.onClose();
      return;
    }

    closeTriggeredModal();
  };

  const defaultRenderEmailStep = ({
    emailForm,
    handleSubmit,
    isLoading,
    buttonText,
    buttonPlacement,
  }: EmailStepRenderProps) => (
    <Form {...emailForm}>
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="space-y-4">
          {buttonPlacement === 'inline' ? (
            <>
              {/* Desktop uses `items-start` so the input column can grow
                  downward (when Nimbus `<FormMessage>` appears below the
                  input) without shifting the submit button. `md:mt-8`
                  offsets the button wrapper past the label height so the
                  button visually aligns with the input on the same row.
                  The validation message is rendered by `<FormMessage>`
                  inside `<FormItem>` (i.e. inside `EmailField`) so the
                  input's `aria-describedby` resolves to a real id and
                  screen readers announce the error. Mobile is a vertical
                  stack: label, input, error, button. */}
              <div className="flex w-full flex-col gap-4 md:flex-row md:items-start">
                <div className="w-full flex-1">
                  <EmailField
                    onPointerDown={() =>
                      analytics.trackFormStarted('email-box-click')
                    }
                    onInput={() => analytics.trackFormStarted('email-box-type')}
                  />
                </div>
                <div className="w-full md:mt-8 md:w-auto">
                  <SubmitButton
                    text={buttonText}
                    isLoading={isLoading}
                    className="h-9 w-full md:w-auto"
                  />
                </div>
              </div>
              <FormFooter
                className="mt-0"
                lang={lang}
                termsUrl={resolvedTermsUrl}
                privacyUrl={resolvedPrivacyUrl}
              />
            </>
          ) : (
            <>
              <div className="w-full">
                <EmailField
                  onPointerDown={() =>
                    analytics.trackFormStarted('email-box-click')
                  }
                  onInput={() => analytics.trackFormStarted('email-box-type')}
                />
              </div>
              <FormFooter
                className="mt-0"
                lang={lang}
                termsUrl={resolvedTermsUrl}
                privacyUrl={resolvedPrivacyUrl}
              />
              <div className="flex justify-end">
                <SubmitButton
                  text={buttonText}
                  isLoading={isLoading}
                  className="w-full md:w-auto"
                />
              </div>
            </>
          )}
        </div>
      </form>
    </Form>
  );

  const renderEmailStepContent = renderEmailStep
    ? renderEmailStep({
        emailForm,
        handleSubmit: emailForm.handleSubmit(handleEmailSubmit, () =>
          analytics.trackFormFailed()
        ),
        isLoading,
        buttonText,
        buttonPlacement,
      })
    : defaultRenderEmailStep({
        emailForm,
        handleSubmit: emailForm.handleSubmit(handleEmailSubmit, () =>
          analytics.trackFormFailed()
        ),
        isLoading,
        buttonText,
        buttonPlacement,
      });

  const renderPaneContent = () => {
    if (step === successStep) {
      return (
        <SuccessScreen
          headline={successHeadline}
          message={successMessage}
          icon={
            successIcon === undefined ? brandConfig.successIcon : successIcon
          }
        />
      );
    }

    return (
      <>
        {step === 0 ? (
          <>
            <FormHeadline
              title={headline}
              subtitle={subHeading}
              showRuler={showRuler}
            />
            {renderEmailStepContent}
          </>
        ) : null}
        {hasQualification && step === 1 && resolvedQualificationStep ? (
          <>
            <FormHeadline
              title={resolvedQualificationStep.headline ?? headline}
              subtitle={
                resolvedQualificationStep.subtitle ?? DEFAULT_STEP2_SUBTITLE
              }
              showRuler={showRuler}
            />
            {resolvedQualificationStep.render({
              qualificationForm: qualificationForm as UseFormReturn<
                Record<string, unknown>
              >,
              handleSubmit: qualificationForm.handleSubmit(
                handleQualificationSubmit,
                () => analytics.trackMicroDataQuestionFailed()
              ),
              isLoading,
              buttonText: resolvedQualificationStep.buttonText,
            } as QualificationStepRenderProps)}
          </>
        ) : null}
        <SubmissionErrorMessage message={submissionErrorMessage} />
      </>
    );
  };

  const defaultWidthClass =
    variant === 'twoColumn' ? 'sm:max-w-[960px]' : 'sm:max-w-[700px]';
  // Width is expressed as a Tailwind utility (not inline style) so that
  // tailwind-merge collapses Nimbus DialogContent's default `sm:max-w-lg` in
  // host apps that compile Tailwind with `important: true` — where an inline
  // style would lose the cascade against an !important author rule. A custom
  // `width` prop falls back to an inline CSS variable consumed by the class.
  const widthClass = width
    ? 'sm:max-w-[var(--bx-forms-modal-max-width)]'
    : defaultWidthClass;
  const widthStyle: React.CSSProperties | undefined = width
    ? ({ '--bx-forms-modal-max-width': width } as React.CSSProperties)
    : undefined;
  const modalWidthClassName = cn(
    widthClass,
    variant === 'twoColumn' && 'overflow-hidden'
  );

  const formPane = (
    <div
      className={cn('w-full space-y-6 p-6 sm:p-8', {
        'rounded-[24px] border border-nimbus-border bg-nimbus-background shadow-nimbus-lg':
          variant === 'minimalNew',
        'bg-nimbus-background': variant !== 'minimalNew',
      })}
    >
      {renderPaneContent()}
    </div>
  );

  return (
    <Nimbus3Scope
      portalBridgeAttributes={portalBridgeAttributes}
      style={{ background: 'transparent' }}
    >
      <FormModal
        open={isOpen}
        title={headline}
        description={subHeading}
        onClose={handleModalClose}
        className={cn(
          modalWidthClassName,
          variant === 'minimalNew' && 'border-0 bg-transparent shadow-none',
          modalClassName
        )}
        contentStyle={widthStyle}
        dataTestId="modal-email-form"
      >
        <section
          aria-label={`${headline} email capture form`}
          onMouseEnter={() => analytics.trackFormMouseover()}
          role="group"
        >
          {variant === 'twoColumn' ? (
            <div className="grid bg-nimbus-background sm:grid-cols-[minmax(0,0.95fr)_minmax(0,1.05fr)]">
              <TwoColumnSidePanel sidePanelContent={sidePanelContent} />
              <div className="min-w-0">{formPane}</div>
            </div>
          ) : (
            formPane
          )}
        </section>
      </FormModal>
    </Nimbus3Scope>
  );
};
